/**
 * FabricPicker — Sauleda kumaş kataloğu seçici
 * Modal, gruplanmış sekmeler, arama ve görsel grid ile kumaş seçimi.
 */
import { useState, useEffect, useRef } from "react";
import { X, Search, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const GROUP_LABELS = {
  Lisos:    "Düz Renkler",
  Clasicos: "Klasik Çizgili",
  Listados: "Fantezi Çizgili",
};

const ALL_TAB = "Tümü";

// ──────────────────────────────────────────────────────────────────
export function FabricPicker({ value, onChange, className = "" }) {
  const [open, setOpen] = useState(false);
  const [fabrics, setFabrics] = useState([]);
  const [tab, setTab] = useState(ALL_TAB);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const searchRef = useRef(null);

  // Fetch fabrics once
  useEffect(() => {
    fetch(`${API}/api/fabrics?catalog=sauleda`)
      .then(r => r.json())
      .then(setFabrics)
      .catch(console.error);
  }, []);

  // Sync selected from parent value
  useEffect(() => {
    if (value && fabrics.length) {
      const found = fabrics.find(f =>
        value.includes(f.code) || value.toLowerCase().includes(f.name.toLowerCase())
      );
      setSelected(found || null);
    } else {
      setSelected(null);
    }
  }, [value, fabrics]);

  function openModal() {
    setOpen(true);
    setQuery("");
    setTimeout(() => searchRef.current?.focus(), 50);
  }

  function pick(fabric) {
    setSelected(fabric);
    onChange(`${fabric.name} (${fabric.code})`);
    setOpen(false);
  }

  function clear(e) {
    e.stopPropagation();
    setSelected(null);
    onChange("");
  }

  // Filter
  const filtered = fabrics.filter(f => {
    const matchTab = tab === ALL_TAB || f.group === tab;
    const q = query.toLowerCase();
    const matchQ = !q || f.name.toLowerCase().includes(q) || f.code.includes(q) || (f.colorDesc || "").toLowerCase().includes(q);
    return matchTab && matchQ;
  });

  const tabs = [ALL_TAB, ...Object.keys(GROUP_LABELS)];

  return (
    <>
      {/* Trigger button */}
      <button
        type="button"
        data-testid="fabric-picker-trigger"
        onClick={openModal}
        className={`zen-input !py-1 flex items-center gap-2 text-left cursor-pointer hover:border-sky-400 transition ${className}`}
      >
        {selected ? (
          <>
            <img
              src={`${API}/api/fabrics/image/${selected.code}`}
              alt={selected.name}
              className="w-7 h-7 rounded object-cover shrink-0 border border-slate-200 dark:border-[#31405D]"
            />
            <span className="flex-1 text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">
              {selected.name}
            </span>
            <span className="text-[10px] text-slate-400">#{selected.code}</span>
            <X
              className="w-3.5 h-3.5 text-slate-400 hover:text-red-500 shrink-0"
              onClick={clear}
            />
          </>
        ) : (
          <>
            <span className="flex-1 text-xs text-slate-400">Sauleda kataloğundan seçin…</span>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          </>
        )}
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white dark:bg-[#0F1826] rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-[#22304A]">
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-800 dark:text-white">Sauleda Kumaş Kataloğu</p>
                <p className="text-[11px] text-slate-400">{fabrics.length} kumaş — düz renkler, klasik ve fantezi çizgililer</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-[#22304A] text-slate-400">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Search + Tabs */}
            <div className="px-5 pt-3 pb-2 space-y-2.5 border-b border-slate-100 dark:border-[#22304A]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Renk adı veya kod ara…"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  className="zen-input !pl-8 !py-1.5 !text-xs w-full"
                />
              </div>
              <div className="flex gap-1.5 overflow-x-auto pb-0.5 no-scrollbar">
                {tabs.map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    className={`whitespace-nowrap px-3 py-1 rounded-full text-[11px] font-semibold transition-all border ${tab === t ? "bg-sky-500 text-white border-sky-500" : "border-slate-200 dark:border-[#22304A] text-slate-500 hover:border-sky-400"}`}
                  >
                    {t === ALL_TAB ? "Tümü" : GROUP_LABELS[t]}
                    <span className="ml-1 opacity-70">
                      ({t === ALL_TAB ? fabrics.length : fabrics.filter(f => f.group === t).length})
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Grid */}
            <div className="overflow-y-auto flex-1 px-5 py-4">
              {filtered.length === 0 ? (
                <p className="text-center text-slate-400 text-sm py-12">Kumaş bulunamadı.</p>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7 gap-2.5">
                  {filtered.map(fabric => {
                    const isSel = selected?.code === fabric.code;
                    return (
                      <button
                        key={fabric.code}
                        type="button"
                        data-testid={`fabric-swatch-${fabric.code}`}
                        onClick={() => pick(fabric)}
                        className={`group relative flex flex-col items-center gap-1 p-1.5 rounded-xl border-2 transition-all hover:scale-105 ${
                          isSel
                            ? "border-sky-500 bg-sky-50 dark:bg-sky-900/20 shadow-md"
                            : "border-transparent hover:border-sky-300 dark:hover:border-sky-600"
                        }`}
                      >
                        {/* Swatch Image */}
                        <div className="w-full aspect-square rounded-lg overflow-hidden bg-slate-100 dark:bg-[#22304A] relative">
                          <img
                            src={`${API}/api/fabrics/image/${fabric.code}`}
                            alt={fabric.name}
                            loading="lazy"
                            className="w-full h-full object-cover"
                            onError={e => {
                              e.target.style.display = "none";
                              e.target.parentElement.style.background = "#e2e8f0";
                            }}
                          />
                          {isSel && (
                            <div className="absolute inset-0 bg-sky-500/20 flex items-center justify-center">
                              <div className="bg-sky-500 rounded-full w-5 h-5 flex items-center justify-center">
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            </div>
                          )}
                        </div>
                        {/* Label */}
                        <p className={`text-[9px] font-semibold text-center leading-tight truncate w-full ${isSel ? "text-sky-600 dark:text-sky-400" : "text-slate-600 dark:text-slate-300"}`}>
                          {fabric.name}
                        </p>
                        <p className="text-[8px] text-slate-400 dark:text-slate-500">{fabric.code}</p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-slate-100 dark:border-[#22304A] flex items-center justify-between">
              <p className="text-[11px] text-slate-400">{filtered.length} kumaş görüntüleniyor</p>
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#22304A] transition">
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
